/* eslint-disable @typescript-eslint/no-explicit-any */

import { EditorServiceImpl } from './editor-service';
import { CancellationTokenSource, Emitter, ProxyIdentifier, RPC_CONTEXT } from './interfaces';
import { NotifyServiceImpl } from './notify-service';
import { RemoteAgentConnectionImpl } from './remote-agent-connection';
import { RPCProtocol, RPCProtocolImpl } from './rpc-protocol';

async function getEditorWindow(editorWindowOrDepth: Window | number): Promise<Window> {
    if (typeof editorWindowOrDepth === 'number') {
        return doGetEditorWindow(window, editorWindowOrDepth);
    } else {
        return editorWindowOrDepth;
    }
}

async function doGetEditorWindow(current: Window, depth: number): Promise<Window> {
    if (depth === 0) {
        return current;
    } else if (depth < 0) {
        return doGetEditorWindow(current.parent, depth + 1);
    } else {
        const frames = await getFrames(current);
        return doGetEditorWindow(frames[0], depth - 1);
    }

}

export function createEmitter(): Emitter<string> {
    const emitter = new Emitter<string>();
    window.addEventListener('message', event => emitter.fire(event.data));
    return emitter;
}

export function computeChannalId(editorWindow: Window): number {
    let channalId = 0;
    for (let index = 0; index < editorWindow.frames.length; index++) {
        const taregtWindow = editorWindow.frames[index];
        if (taregtWindow === window) {
            return channalId;
        }
        channalId++;
    }

    let current = editorWindow.parent;
    while (window !== current) {
        console.log(channalId);
        channalId++;
        current = current.parent;
    }
    return channalId;
}

export async function createClient(editorWindowOrDepth: Window | number): Promise<RPCProtocol> {
    const targetWindow = await getEditorWindow(editorWindowOrDepth);
    const rpc = await createRpc(-1, targetWindow);
    rpc.set(RPC_CONTEXT.EditorService, new EditorServiceImpl());
    rpc.set(RPC_CONTEXT.RemoteAgentConnection, new RemoteAgentConnectionImpl());
    rpc.set(RPC_CONTEXT.NotifyService, new NotifyServiceImpl());
    return rpc;
}

export async function createRpc(channalId: number, targetWindow: Window): Promise<RPCProtocolImpl> {
    const emitter = createEmitter();
    const rpc = new RPCProtocolImpl(channalId, {
        onMessage: emitter.event,
        send: (message: string) => {
            targetWindow.postMessage(message, { targetOrigin: '*' });
        },
    });
    return rpc;
}

async function getFrames(taregtWindow: Window): Promise<Window> {
    const cancellationTokenSource = new CancellationTokenSource();
    return Promise.race([doGetFrames(taregtWindow, cancellationTokenSource), new Promise<Window>(resolve => {
        setTimeout(() => {
            cancellationTokenSource.cancel();
            resolve(taregtWindow.frames);
        }, 3000);
    })]);
}

async function doGetFrames(taregtWindow: Window, cancellationTokenSource: CancellationTokenSource): Promise<Window> {
    if (taregtWindow.frames.length > 0 || cancellationTokenSource.token.isCancellationRequested) {
        return taregtWindow.frames;
    } else {
        return new Promise<Window>(resolve => {
            setTimeout(() => {
                doGetFrames(taregtWindow, cancellationTokenSource).then(resolve);
            }, 0);
        });
    }
}

export async function createServer(): Promise<RPCProtocol> {
    const rpcList: RPCProtocol[] = [];
    let channalId = 0;
    await new Promise<void>((resolve => {
        setTimeout(resolve, 500);
    }));
    const frames = await getFrames(window);
    for (let index = 0; index < frames.length; index++) {
        const rpc = await createRpc(channalId, frames[index]);
        rpcList.push(rpc);
        channalId++;
    }
    let current: Window = window;
    do {
        current = current.parent;
        if (current !== window) {
            const rpc = await createRpc(channalId, current);
            rpcList.push(rpc);
            channalId++;
        }
    } while (top !== current);
    return {
        dispose(): void {
            for (const item of rpcList) {
                item.dispose();
            }
        },

        getProxy<T>(proxyId: ProxyIdentifier<T>): T {

            const handler = {
                get: (target: any, name: string) => {
                    target[name] = (...myArgs: any[]) => {
                        let result;
                        for (const item of rpcList) {
                            const proxy: any = item.getProxy(proxyId);
                            try {
                                result = proxy[name](...myArgs);
                            } catch (error) {
                                if (!error?.message?.includes('Unknown actor')) {
                                    throw error;
                                }
                            }
                        }
                        return result;
                    };
                    return target[name];
                }
            };
            return new Proxy(Object.create(null), handler); // eslint-disable-line no-null/no-null
        },

        getLocal<T>(proxyId: ProxyIdentifier<T>): T {
            return rpcList[0].getLocal(proxyId);
        },

        set<T, R extends T>(identifier: ProxyIdentifier<T>, instance: R): R {
            for (const item of rpcList) {
                item.set(identifier, instance);
            }
            return instance;
        },
    };
}
